'use client';

import { useEffect, useState } from 'react';
import type { Game } from './games';
import { popularityRank } from './gamePlays';
import type { GamePulse, GamePulses } from './gamePulse';

/**
 * Three things a player can say about a game (owner's spec, 2026-10-09):
 *
 *   ❤  Favourite — puts it in "Your favourites". Personal; never added up.
 *   👍 Like      — a vote.
 *   👎 Dislike   — a vote.
 *
 * A game exists by its post (owner, 2026-10-14): every game has one official
 * post in c/games, and its likes and dislikes ARE that post's upvotes and
 * downvotes (backend routes/games.ts › Votes). So, as on Reddit, liking needs
 * an account: signed out, pressing a thumb goes to sign-in and comes back.
 * One vote per person per game: pressing the lit one takes it back, pressing
 * the other moves it. The counts are re-read every POLL_MS while the shelf or
 * a game is open; your own press moves them at once and the server's answer
 * then replaces the guess. `post` links to the game's post (its comments).
 *
 * Favourites stay personal: the account's when signed in, otherwise this
 * browser's (a random id kept in localStorage and sent as `X-GT-Voter`), moved
 * to the account the first time it is seen signed in.
 *
 * ── Carried over from the old thumbs ──────────────────────────────────────
 * The old shelf had two thumbs, and a thumb-up put the game in Favourites. A
 * game somebody had put there is therefore read as BOTH a favourite and a
 * like (owner: "regard them that they clicked like"); a thumb-down stays a
 * dislike. That conversion runs once per browser and is sent to the server.
 */

export type Vote = 'like' | 'dislike';
export interface GameStat { likes: number; dislikes: number; post?: { slug: string; id: string; comments: number } | null }
export type GameStats = Record<string, GameStat>;
interface Mine { votes: Record<string, Vote>; favs: string[] }

export const POLL_MS = 3000;

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';
const VOTES_KEY = 'gt_game_votes';
const FAVS_KEY = 'gt_game_favs';
const VOTER_KEY = 'gt_voter';
/** Set while this browser still has votes the server has not been told about. */
const PUSH_KEY = 'gt_game_votes_push';
/** The previous shelf's storage, converted once and then removed. */
const LEGACY_RATINGS = 'gt_game_ratings';
const LEGACY_SEEDED = 'gt_game_defaults_v1';
const LEGACY_UNSEEDED = 'gt_game_defaults_removed';

let mine: Mine = { votes: {}, favs: [] };
let stats: GameStats = {};
let ready = false;
/** The signed-in account, if any (set by useGameVotes): votes need one. */
let account: string | null = null;
/** Requests in flight: while any is, a poll must not overwrite the optimistic state. */
let pending = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

const isVote = (v: unknown): v is Vote => v === 'like' || v === 'dislike';
const GAME_ID = /^[a-z0-9]{1,24}$/;

function store(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

function voterId(): string {
  const ls = store();
  let id = ls?.getItem(VOTER_KEY) || '';
  if (!/^[a-z0-9-]{16,64}$/.test(id)) {
    id = (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`).toLowerCase();
    try { ls?.setItem(VOTER_KEY, id); } catch { /* private mode: a new id per visit */ }
  }
  return id;
}

/** Read this browser's state once, converting the old thumbs if they are still there. */
function init() {
  if (ready) return;
  ready = true;
  const ls = store();
  if (!ls) return;
  try {
    const votes: Record<string, Vote> = {};
    const v = JSON.parse(ls.getItem(VOTES_KEY) || '{}');
    if (v && typeof v === 'object') for (const [id, x] of Object.entries(v)) if (GAME_ID.test(id) && isVote(x)) votes[id] = x;
    const f = JSON.parse(ls.getItem(FAVS_KEY) || '[]');
    const favs: string[] = Array.isArray(f) ? f.filter((id) => typeof id === 'string' && GAME_ID.test(id)) : [];

    const legacy = ls.getItem(LEGACY_RATINGS);
    if (legacy) {
      const old = JSON.parse(legacy) || {};
      // Likes the old shelf invented for everybody are not anybody's choice:
      // drop them first, exactly as that shelf's own clean-up did.
      if (!ls.getItem(LEGACY_UNSEEDED)) for (const id of (ls.getItem(LEGACY_SEEDED) || '').split(',')) if (old[id] === 'like') delete old[id];
      for (const [id, x] of Object.entries(old)) {
        if (!GAME_ID.test(id)) continue;
        if (x === 'like') { if (!(id in votes)) votes[id] = 'like'; if (!favs.includes(id)) favs.push(id); }
        if (x === 'dislike' && !(id in votes)) votes[id] = 'dislike';
      }
      ls.removeItem(LEGACY_RATINGS);
      ls.setItem(LEGACY_UNSEEDED, '1');
      if (Object.keys(old).length) ls.setItem(PUSH_KEY, '1');
    }
    mine = { votes, favs };
    save();
  } catch { /* unreadable storage: start clean */ }
}

function save() {
  const ls = store();
  if (!ls) return;
  try {
    ls.setItem(VOTES_KEY, JSON.stringify(mine.votes));
    ls.setItem(FAVS_KEY, JSON.stringify(mine.favs));
  } catch { /* private mode */ }
}

function emit() { listeners.forEach((fn) => fn()); }

function adoptMine(m: unknown) {
  if (!m || typeof m !== 'object') return;
  const mm = m as { votes?: Record<string, unknown>; favs?: unknown };
  const votes: Record<string, Vote> = {};
  for (const [id, x] of Object.entries(mm.votes ?? {})) if (isVote(x)) votes[id] = x;
  const favs = Array.isArray(mm.favs) ? mm.favs.filter((x): x is string => typeof x === 'string') : [];
  mine = { votes, favs };
  save();
}

async function post(items: { game: string; vote?: Vote | null; fav?: boolean }[]) {
  pending++;
  try {
    const r = await fetch(`${API}/api/games/vote`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-GT-Voter': voterId() },
      body: JSON.stringify({ items }),
    });
    const j = r.ok ? await r.json() : null;
    // Refused (signed out, banned, removed post…): the next poll puts the true counts back.
    if (!r.ok) return false;
    if (j?.stats) stats = { ...stats, ...j.stats };
    if (pending === 1 && j?.mine) adoptMine(j.mine);
    return !!j?.ok;
  } catch {
    return false;
  } finally {
    pending--;
    emit();
  }
}

async function load() {
  if (typeof document !== 'undefined' && document.hidden) return;
  const ls = store();
  // Votes this browser has that the server does not (the old thumbs): send
  // them first, once, so the totals include them.
  if (ls?.getItem(PUSH_KEY)) {
    // Votes need an account now (they are votes on the game's post); favourites do not.
    const items = [
      ...(account ? Object.entries(mine.votes).map(([game, vote]) => ({ game, vote, fav: mine.favs.includes(game) })) : []),
      ...mine.favs.filter((g) => !account || !(g in mine.votes)).map((game) => ({ game, fav: true })),
    ];
    ls.removeItem(PUSH_KEY);
    if (items.length) await post(items);
  }
  try {
    const r = await fetch(`${API}/api/games/stats`, { credentials: 'include', cache: 'no-store', headers: { 'X-GT-Voter': voterId() } });
    if (!r.ok) return;
    const j = await r.json();
    if (pending) return;   // a press is on its way; its answer is newer than this
    if (j?.stats && typeof j.stats === 'object') stats = j.stats;
    if (j?.mine) adoptMine(j.mine);
    emit();
  } catch { /* offline: keep the last numbers */ }
}

/** Like, dislike, or take the vote back (null). Moves the count at once. */
export function setVote(id: string, next: Vote | null) {
  init();
  if (!account) {
    if (typeof window !== 'undefined') window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    return;
  }
  const prev = mine.votes[id];
  if (prev === next || (!prev && next === null)) return;
  const cur: GameStat = { ...(stats[id] ?? { likes: 0, dislikes: 0 }) };
  if (prev === 'like') cur.likes = Math.max(0, cur.likes - 1);
  if (prev === 'dislike') cur.dislikes = Math.max(0, cur.dislikes - 1);
  if (next === 'like') cur.likes += 1;
  if (next === 'dislike') cur.dislikes += 1;
  stats = { ...stats, [id]: cur };
  const votes = { ...mine.votes };
  if (next) votes[id] = next; else delete votes[id];
  mine = { ...mine, votes };
  save();
  emit();
  void post([{ game: id, vote: next }]);
}

/** Put a game in, or take it out of, "Your favourites". */
export function toggleFav(id: string) {
  init();
  const on = !mine.favs.includes(id);
  mine = { ...mine, favs: on ? [...mine.favs, id] : mine.favs.filter((g) => g !== id) };
  save();
  emit();
  void post([{ game: id, fav: on }]);
}

/** Live counts plus this voter's own votes and favourites. */
export function useGameVotes(me: string | null) {
  const [, setTick] = useState(0);
  account = me;
  useEffect(() => {
    init();
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    fn();
    load();
    if (!timer) timer = setInterval(load, POLL_MS);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      listeners.delete(fn);
      document.removeEventListener('visibilitychange', onVis);
      if (!listeners.size && timer) { clearInterval(timer); timer = null; }
    };
    // Re-read when the account changes: a sign-in moves this browser's votes to it.
  }, [me]);
  return { stats, votes: mine.votes, favs: mine.favs, signedIn: !!me };
}

/**
 * The shelf in this player's order: their Favourites first (in the order
 * given), then every other game most played first — the live totals off the
 * pulse (lib/gamePulse): counted plays, then players, then opens. Games nobody
 * has played sink to the bottom, where the old logged order (popularityRank)
 * still splits the ties, and is the whole order until the pulse has loaded.
 */
export function rankGames(games: Game[], favourites: readonly string[] = [], played: GamePulses = {}): Game[] {
  const fav = (id: string) => { const i = favourites.indexOf(id); return i < 0 ? Infinity : i; };
  const n = (id: string, k: keyof GamePulse) => played[id]?.[k] ?? 0;
  return [...games].sort((a, b) =>
    fav(a.id) - fav(b.id) ||
    n(b.id, 'plays') - n(a.id, 'plays') ||
    n(b.id, 'players') - n(a.id, 'players') ||
    n(b.id, 'clicks') - n(a.id, 'clicks') ||
    popularityRank(a.id) - popularityRank(b.id));
}
